import { h, Component } from 'preact';
import { RenderTarget } from './render_target';
import { normalize } from 'utilities/math';
import { Loader } from 'rendering/components/loader';
import { accessibleContainer } from 'utilities/accessibility';
import { computeVideoBreakpoint } from 'utilities/rendering';
import { BASE_WIDTH, DEFAULT_ASPECT_RATIO } from 'configuration/defaults';
import { ANNOTATION_TARGETS, MEDIA_CHANGE_STATES } from 'constants';
import { PLAYER_EVENT } from 'constants/events';

export class PlayerTarget extends RenderTarget {
    constructor(props) {
        super(props);

        const { basePhysicalWidth, basePhysicalHeight } = this.props.contentDimensions.player;

        this.state.width = this.props.width || (basePhysicalWidth || BASE_WIDTH);
        this.state.height = this.props.height;
        this.state.scale = this.state.width / (basePhysicalWidth || BASE_WIDTH);
        this.state.scaleFinalized = false;

        props.bus.on(PLAYER_EVENT.TIME_UPDATE, this.onTimeUpdate);
        props.bus.on(PLAYER_EVENT.SIZE_CHANGE, this.onSizeChange);
        props.bus.on(PLAYER_EVENT.HIGH_RESOLUTION_TIME_UPDATE, this.onHighResTimeUpdate);
        props.bus.on(PLAYER_EVENT.MEDIA_CHANGE, this.onMediaChange);
        props.bus.once(PLAYER_EVENT.LOADED_METADATA, this.onSizeChange);
    }

    onSizeChange = (event, player) => {
        const { basePhysicalWidth, basePhysicalHeight } = this.props.contentDimensions.player;

        let width = player.containerDimensions.width,
            height = player.containerDimensions.height,
            scale;

        if (player.videoDimensions && player.videoDimensions.width) {
            width = player.videoDimensions.width / player.videoDimensions.height * height;
        }

        this.setScale(width, basePhysicalWidth);

        scale = this.getScale(width, basePhysicalWidth);

        this.setState({ width, height, scaleFinalized: true });
        this.props.bus.emit(PLAYER_EVENT.SCALE_CHANGE, scale);

    }

    onTimeUpdate = (event, player) => {
        if (player) {
            this.setState({ currentTime: player.currentTime });
        }
    }

    getPlayerContentDimensions = (prevProps) => {
        return prevProps.contentDimensions && prevProps.contentDimensions.player || {};
    }

    get basePhysicalWidth() {
        const { props } = this; 
        return props.contentDimensions && props.contentDimensions.player && props.contentDimensions.player.basePhysicalWidth || null; 
    }

    componentDidUpdate(prevProps, prevState) {
        const prevPhysicalWidth = this.getPlayerContentDimensions(prevProps).basePhysicalWidth || null;
        const newPhysicalWidth = this.basePhysicalWidth; 
        const { scaleFinalized } = this.state; 

        if (!scaleFinalized && !prevPhysicalWidth) {
            this.setState({ scaleFinalized: true });
        }

        if (prevPhysicalWidth !== newPhysicalWidth) {;
            this.setScale(this.state.width, newPhysicalWidth);
        }

    }

    onHighResTimeUpdate = currentTime => this.setState({ currentTime })

    onMediaChange = ({ status }) => this.setState({ mediaLoadingStatus: status })

    get containerAttributes () {
        return {
            ...accessibleContainer,
            style: {},
            className: 'hapyak-annotation-container active hapyak-root'
        }
    }

    get scaleableContainerAttrs() {
        const { className } = this.containerAttributes;

        return {
            ...this.containerAttributes,
            style: this.containerStyles,
            className: `${className} scalable-container`,
            ['data-scale-final']: this.state.scaleFinalized
        }
    }

    get nonScalableContainerAttributes() {
        return {
            ...this.containerAttributes,
            'data-scale-final': true
        }
    }

    get videoContainerDimensions() {
        return this.props.playerInterface?.containerDimensions || {};
    }

    render() {
        const [controlBar] = this.annotationList.filter(annotation => annotation.type === 'controls');
        const { theme = '', options = {} } = controlBar?.properties || {};
        const media = this.props.data.media && this.props.data.media.length ? this.props.data.media[0] : { url: '', thumbnail: '' };
        const { project = {} } = this.props.data;
        const { posterImage = '' } = project;
        const playerSize = computeVideoBreakpoint(this.videoContainerDimensions);

        const VideoRenderer = this.props.playerInterface.Renderable;
        const attributes = {
            'key': 'video',
            'src': media.url,
            'autoplay': this.props.autoplay,
            'muted': this.props.muted,
            'controls': this.props.controls,
            'captions': this.props.captions,
            'loop': this.props.loop,
            'style': this.targetStyles,
            'className': 'player_target',
            'data-target': 'player_target',
            'playbackRates': options.playbackRates,
            'controlThemeSkin': theme,
            'data-player-size': playerSize,
            'data-annotation-count': this.annotations.length
        };

        if (this.state.currentTime <= 0) {
            attributes['poster'] = posterImage;
        } else {
            attributes['data-show-poster'] = "false";
        }

        return (
            <VideoRenderer { ...attributes }>
                <div {...this.scaleableContainerAttrs}>
                    { this.scalableAnnotations }
                </div>
                <div {...this.nonScalableContainerAttributes}>
                    { this.nonScalableAnnotations }
                </div>
                { this.loader }
            </VideoRenderer>
        );
    }

    getScale = (width, physicalWidth = BASE_WIDTH) => {
        return width / physicalWidth;
    }

    setScale = (width, physicalWidth = BASE_WIDTH) => {
        const scale = this.getScale(width, physicalWidth);
        
        this.setState({
            scale: scale,
            scaleFinalized: true
        });  
    }

    get loader() {
        const styles = {};

        if (this.state.mediaLoadingStatus === MEDIA_CHANGE_STATES.LOADING) {
            styles.opacity = 1;
        }

        return (
            <div className="blocking-loader" style={ styles }>
                <Loader background="rgba(43,51,63,.7)" foreground="rgba(255,255,255,0.85)" size="48"/>
            </div>
        )
    }

    get aspectRatio() {
        const { contentDimensions = {} } = this.props;

        if (contentDimensions.player && contentDimensions.player.aspect) {
            return normalize(contentDimensions.player.aspect);
        }

        const aspectRatio = this.state.height / this.state.width;
        return aspectRatio && !isNaN(aspectRatio) ? aspectRatio : DEFAULT_ASPECT_RATIO;
    }

    get containerStyles() {
        const { basePhysicalWidth = BASE_WIDTH } = this.props.contentDimensions.player;
        
        const styles = {
            width: `${basePhysicalWidth}px`,
            height: `${basePhysicalWidth * this.aspectRatio}px`,
            transform: `scale(${this.state.scale})`
        };

        return styles;
    }
}
